import React, { useState, useRef, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ScrollView,
} from "react-native";

import { observer, Observer } from "mobx-react";

import Fonts from "../../Resources/Fonts";
import Colors from "../../Resources/Color";
import GlobalStyles from "../../Resources/Styles";
import Block from "../Firestore/Block";

import { v4 as uuidv4 } from "uuid";
import { useDevice } from "../_layout";
import Workout from "../Firestore/Workout";

import { useTranslation } from "react-i18next";
import { ProtocolTitles } from "../../Resources/Constants";
import { FontAwesome6, Ionicons } from "@expo/vector-icons";
import { getWeekNumber, getSundayOfWeek } from "../utils/dateUtils";
import DeleteBlockView from "./DeleteBlockView";

const AddCalendarBlockView = observer(
  ({ isVisible, onClose, block, client, coach, totalWeeks, startDate }) => {
    const { t, i18n } = useTranslation();
    const { isMobile } = useDevice();

    const [showingDeleteBlock, setShowingDeleteBlock] = useState(false);

    const [blockName, setBlockName] = useState(
      block && block.name ? block.name : ""
    );
    const predefinedText =
      block && block.name ? block.name : t("newBlock-string");

    const text = block ? "editBlock-string" : "createBlock-string";
    const modalTitle = t(text).toUpperCase();

    const [number, setNumber] = useState(
      block && block.totalWeeks
        ? block.totalWeeks
        : totalWeeks
        ? totalWeeks
        : null
    );

    const [labelName, setLabelName] = useState(
      block && block.label ? block.label : ""
    );

    const [isNameHovered, setIsNameHovered] = useState(false);
    const [isWeeksHovered, setIsWeeksHovered] = useState(false);
    const [isFolderHovered, setIsFolderHovered] = useState(false);

    const blockNameInputRef = useRef(null);

    useEffect(() => {
      if (isVisible && blockNameInputRef.current) {
        setTimeout(() => {
          blockNameInputRef.current?.focus();
        }, 100);
      }
    }, [isVisible]);

    const blockStartDate = block ? block.initialDate : startDate;

    // Helpers to limit totalWeeks to the number of free weeks available from the start
    const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
    const mondayAtHour = (date, hour = 12) => {
      if (!date) return null;
      const d = new Date(date);
      const offset = (d.getDay() + 6) % 7; // Monday = 0
      d.setDate(d.getDate() - offset);
      d.setHours(hour, 0, 0, 0);
      return d;
    };
    const getInterval = (b) => {
      if (!b?.initialDate) return null;
      const startMon = mondayAtHour(b.initialDate, 12);
      const weeks = Math.max(
        0,
        Number(b?.effectiveTotalWeeks ?? b?.totalWeeks) || 0
      );
      const endExclusive = new Date(startMon.getTime() + weeks * MS_PER_WEEK);
      return { id: b.id, start: startMon, endExclusive };
    };

    const maxFreeWeeksFromStart = (() => {
      const startMon = mondayAtHour(blockStartDate, 12);
      if (!startMon || !client) {
        return 24;
      }
      const others = (client.blocks || [])
        .filter((b) => !block || b.id !== block.id)
        .map(getInterval)
        .filter(Boolean);

      // Find the nearest next block start after our start
      let nextStart = null;
      for (const iv of others) {
        if (iv.start > startMon) {
          if (nextStart === null || iv.start < nextStart) nextStart = iv.start;
        }
      }
      if (!nextStart) {
        return 24; // effectively unlimited here, UI cap applies
      }
      // Use rounding to avoid DST-caused off-by-one (e.g., CET->CEST shifts ~1h)
      const diffWeeks = Math.round(
        (nextStart.getTime() - startMon.getTime()) / MS_PER_WEEK
      );
      return Math.max(0, diffWeeks);
    })();

    const MAX_UI_WEEKS = 24;
    const allowedMaxWeeks = Math.max(
      1,
      Math.min(maxFreeWeeksFromStart, MAX_UI_WEEKS)
    );

    // Validation for totalWeeks (must be after allowedMaxWeeks is defined)
    const isNumberValid =
      typeof number === "number" &&
      number !== null &&
      !isNaN(number) &&
      (block ? number >= block.getWeeks : number >= 1) &&
      number <= allowedMaxWeeks;

    const minAllowedWeeks = block ? block.getWeeks : 1;

    // Preview end date uses the allowed cap
    const previewWeeks = number;
    const blockEndDate =
      blockStartDate && previewWeeks
        ? (() => {
            const d = new Date(blockStartDate);
            d.setDate(d.getDate() + (previewWeeks - 1) * 7);
            const sunday = getSundayOfWeek(d, true);
            return sunday;
          })()
        : null;

    const handleChangeWeek = (text) => {
      const sanitizedText = String(text ?? "")
        .replace(/\D/g, "")
        .slice(0, 2);
      if (sanitizedText === "") {
        setNumber(null);
        return;
      }
      const parsedNumber = parseInt(sanitizedText, 10);
      if (parsedNumber <= MAX_UI_WEEKS) {
        setNumber(isNaN(parsedNumber) ? null : parsedNumber);
      }
    };

    const handleIncrementWeeks = () => {
      const fallbackValue = Math.max(
        minAllowedWeeks,
        Math.min(6, allowedMaxWeeks)
      );
      const nextValue = number ?? fallbackValue;
      setNumber(Math.min(nextValue + 1, allowedMaxWeeks));
    };

    const handleDecrementWeeks = () => {
      const fallbackValue = Math.max(
        minAllowedWeeks,
        Math.min(6, allowedMaxWeeks)
      );
      const nextValue = number ?? fallbackValue;
      setNumber(Math.max(nextValue - 1, minAllowedWeeks));
    };

    const cancel = () => {
      setBlockName(block ? block.name : "");
      setNumber(null);
      onClose();
    };

    const handleKeyPress = (e) => {
      if (e.key === "Enter" || e.keyCode === 13) {
        if (isNumberValid) {
          // Trigger save if valid
          const newName = blockName === "" ? predefinedText : blockName;
          if (!block) {
            if (client) {
              const newBlock = new Block(
                uuidv4(),
                newName,
                "",
                [],
                false,
                false,
                client,
                1,
                number
                  ? Math.min(number, allowedMaxWeeks)
                  : Math.min(6, allowedMaxWeeks),
                labelName
              );
              const newWorkout = new Workout(
                uuidv4(),
                null,
                i18n.language === "es" ? "Día 1" : "Day 1",
                0,
                1,
                false,
                newBlock,
                []
              );
              newBlock.workouts.push(newWorkout);
              if (startDate) {
                newBlock.setProperty("calendarStartDate", startDate);
              }
              client.blocks.push(newBlock);
              client.updateClientTransactionData(newBlock.id);
              client.startOrResetBatchTimer();
            }
          } else {
            block.setProperty("name", newName);
            block.setProperty(
              "totalWeeks",
              number
                ? Math.min(number, allowedMaxWeeks)
                : Math.min(6, allowedMaxWeeks)
            );
            block.setProperty("label", labelName);
          }
          onClose();
        }
      }
    };

    const Buttons = ({ bool }) => {
      const textBtn = bool ? "save-string" : "cancel-string";
      const [isHovered, setIsHovered] = useState(false);

      const Save = () => {
        if (bool && !isNumberValid) return; // Prevent save if invalid
        const newName = blockName === "" ? predefinedText : blockName;
        if (!block) {
          if (client) {
            const newBlock = new Block(
              uuidv4(),
              newName,
              "",
              [],
              false,
              false,
              client,
              1,
              number
                ? Math.min(number, allowedMaxWeeks)
                : Math.min(6, allowedMaxWeeks),
              labelName
            );
            const newWorkout = new Workout(
              uuidv4(),
              null,
              i18n.language === "es" ? "Día 1" : "Day 1",
              0,
              1,
              false,
              newBlock,
              []
            );
            // newBlock.setProperty("isShowingDashboard", true);
            newBlock.workouts.push(newWorkout);

            if (startDate) {
              newBlock.setProperty("calendarStartDate", startDate);
            }

            client.blocks.push(newBlock);

            client.updateClientTransactionData(newBlock.id);
            client.startOrResetBatchTimer();
          } else {
            if (coach) {
              const nextOrder = Array.isArray(coach.blockLibrary)
                ? coach.blockLibrary.reduce((maxOrder, entry) => {
                    const parsed = Number(entry?.order);
                    if (!Number.isFinite(parsed)) return maxOrder;
                    return parsed > maxOrder ? parsed : maxOrder;
                  }, -1) + 1
                : 0;

              const newBlock = new Block(
                uuidv4(),
                newName,
                "",
                [],
                false,
                false,
                null,
                1,
                number
                  ? Math.min(number, allowedMaxWeeks)
                  : Math.min(6, allowedMaxWeeks),
                labelName,
                undefined,
                coach,
                nextOrder,
              );
              const newWorkout = new Workout(
                uuidv4(),
                null,
                i18n.language === "es" ? "Día 1" : "Day 1",
                0,
                1,
                false,
                newBlock,
                []
              );
              // newBlock.setProperty("isShowingDashboard", true);
              newBlock.toggleProtocolSeriesShown(
                ProtocolTitles.PRIMARY_BENCH_PRESS
              );
              newBlock.toggleProtocolSeriesShown(
                ProtocolTitles.PRIMARY_DEADLIFT
              );
              newBlock.toggleProtocolSeriesShown(ProtocolTitles.PRIMARY_SQUAT);
              newBlock.workouts.push(newWorkout);

              coach.blockLibrary.push(newBlock);

              coach.updateCoachTransactionBlocks(newBlock.id);
              coach.startOrResetBatchTimer();
            }
          }
        } else {
          block.setProperty("name", newName);
          block.setProperty(
            "totalWeeks",
            number
              ? Math.min(number, allowedMaxWeeks)
              : Math.min(6, allowedMaxWeeks)
          );
          block.setProperty("label", labelName);
        }
        onClose();
      };

      if (bool) {
        // Save button
        return (
          <TouchableOpacity
            onPress={Save}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ opacity: !isNumberValid ? 0.5 : isHovered ? 0.8 : 1 }}
          >
            <View
              style={{
                backgroundColor: Colors.bubbleColor,
                borderRadius: 14,
                borderWidth: 1,
                justifyContent: "center",
                borderColor: Colors.bubbleColor,
                width: 80,
                height: 35,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  textAlignVertical: "center",
                  textAlign: "center",
                  color: Colors.whiteColor,
                }}
              >
                {t(textBtn)}
              </Text>
            </View>
          </TouchableOpacity>
        );
      } else {
        // Cancel button
        return (
          <TouchableOpacity
            onPress={cancel}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={{
                backgroundColor: isHovered
                  ? Colors.redColor
                  : Colors.whiteColor,
                borderRadius: 14,
                borderWidth: 1,
                justifyContent: "center",
                borderColor: "red",
                width: 80,
                height: 35,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  textAlignVertical: "center",
                  textAlign: "center",
                  color: isHovered ? "white" : Colors.redColor,
                }}
              >
                {t(textBtn)}
              </Text>
            </View>
          </TouchableOpacity>
        );
      }
    };

    const deleteBlock = () => {
      if (block) {
        client.deleteBlock(block);
      }
      setShowingDeleteBlock(false);
      onClose();
    };

    const DeleteBlockButton = () => {
      const [isHovered, setIsHovered] = useState(false);
      return (
        <TouchableOpacity
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onPress={() => setShowingDeleteBlock(true)}
        >
          <Ionicons
            name={isHovered ? "trash" : "trash-outline"}
            size={20}
            color={isHovered ? "red" : Colors.grayColor}
            style={
              {
                // paddingLeft: 10,
              }
            }
          />
        </TouchableOpacity>
      );
    };

    const FolderSuggestionButton = ({ label }) => {
      const normalizedCurrentLabel = String(labelName ?? "")
        .trim()
        .toLowerCase();
      const isSelected = label.toLowerCase() === normalizedCurrentLabel;
      const [isHovered, setIsHovered] = useState(false);
      const isActiveHover = isHovered && !isSelected;
      const folderIconColor = isSelected
        ? Colors.whiteColor
        : isActiveHover
        ? Colors.blackColor
        : Colors.darkgrayColor;

      return (
        <TouchableOpacity
          onPress={() => setLabelName(label)}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ maxWidth: "100%" }}
        >
          <View
            style={[
              styles.folderSuggestionChip,
              isSelected && styles.folderSuggestionChipSelected,
              isActiveHover && styles.folderSuggestionChipHovered,
            ]}
          >
            <View style={styles.folderSuggestionContentRow}>
              <Ionicons
                name="folder-outline"
                size={12}
                color={folderIconColor}
                style={styles.folderSuggestionIcon}
              />
              <Text
                numberOfLines={1}
                style={[
                  styles.folderSuggestionText,
                  isSelected && styles.folderSuggestionTextSelected,
                  isActiveHover && styles.folderSuggestionTextHovered,
                ]}
              >
                {label}
              </Text>
            </View>
          </View>
        </TouchableOpacity>
      );
    };

    if (showingDeleteBlock && block) {
      return (
        <DeleteBlockView
          isVisible={showingDeleteBlock}
          onClose={() => setShowingDeleteBlock(false)}
          title={"deleteBlockAlertTitle-string"}
          text={"deleteBlockAlertText-string"}
          action={deleteBlock}
          nameOfBlock={block.name}
        />
      );
    }
    return (
      <Modal
        animationType="fade"
        transparent={true}
        visible={isVisible}
        onRequestClose={onClose}
      >
        <View style={GlobalStyles.centeredView}>
          <TouchableOpacity
            onPress={onClose}
            style={GlobalStyles.touchableBackground}
          >
            <View />
          </TouchableOpacity>
          <View
            style={{
              position: "absolute",
              maxHeight: "90%",
              maxWidth: "90%",
            }}
            onStartShouldSetResponder={() => false}
          >
            <View style={[styles.modalView, { width: 400, flexShrink: 1 }]}>
              <ScrollView
                showsVerticalScrollIndicator={false}
                style={{ width: "100%", maxHeight: "100%" }}
                contentContainerStyle={{
                  alignItems: "center",
                  justifyContent: "center",
                  flexGrow: 1,
                }}
              >
                <View
                  style={{
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Text style={Fonts.bold18Centered}>{modalTitle}</Text>
                  <TextInput
                    ref={blockNameInputRef}
                    style={[
                      GlobalStyles.textInputStyle,
                      {
                        borderRadius: 12,
                        borderColor: isNameHovered
                          ? "rgba(0, 0, 0, 0.45)"
                          : Colors.borderColor,
                        fontSize: isMobile ? 16 : undefined,
                      },
                    ]}
                    placeholder={predefinedText}
                    placeholderTextColor={Colors.grayColor}
                    value={blockName}
                    onChangeText={setBlockName}
                    maxLength={40}
                    onMouseEnter={() => !isMobile && setIsNameHovered(true)}
                    onMouseLeave={() => setIsNameHovered(false)}
                    onKeyPress={handleKeyPress}
                  />

                  <View style={styles.weeksRow}>
                    <Text style={Fonts.medium14}>{t("weeksDots-string")}</Text>

                    <View style={styles.weeksColumn}>
                      <View
                        style={[
                          styles.weeksControl,
                          isWeeksHovered && styles.weeksControlHovered,
                        ]}
                        onMouseEnter={() => !isMobile && setIsWeeksHovered(true)}
                        onMouseLeave={() => setIsWeeksHovered(false)}
                      >
                        <TouchableOpacity
                          style={styles.weekArrowButton}
                          onPress={handleDecrementWeeks}
                        >
                          <Ionicons
                            name="remove"
                            size={16}
                            color={Colors.darkgrayColor}
                          />
                        </TouchableOpacity>

                        <TextInput
                          style={[
                            styles.weekInput,
                            {
                              color: !isNumberValid ? "red" : Colors.blackColor,
                              fontSize: isMobile ? 16 : undefined,
                              borderColor: isWeeksHovered
                                ? "rgba(0, 0, 0, 0.45)"
                                : Colors.borderColor,
                            },
                          ]}
                          placeholder={"6"}
                          placeholderTextColor={Colors.grayColor}
                          value={
                            number !== null && number !== undefined
                              ? number.toString()
                              : ""
                          }
                          onChangeText={handleChangeWeek}
                          onKeyPress={handleKeyPress}
                          inputMode="numeric"
                          keyboardType="numeric"
                          maxLength={2}
                        />

                        <TouchableOpacity
                          style={styles.weekArrowButton}
                          onPress={handleIncrementWeeks}
                        >
                          <Ionicons
                            name="add"
                            size={16}
                            color={Colors.darkgrayColor}
                          />
                        </TouchableOpacity>
                      </View>

                      {blockStartDate && blockEndDate && (
                        <View style={styles.weekRangePreviewRow}>
                          <Text style={styles.weekRangePreviewText}>{`W${getWeekNumber(
                            blockStartDate
                          )}`}</Text>
                          <FontAwesome6
                            name="arrow-right-long"
                            size={10}
                            color={Colors.grayColor}
                          />
                          <Text
                            style={styles.weekRangePreviewText}
                          >{`W${getWeekNumber(blockEndDate)}`}</Text>
                        </View>
                      )}
                    </View>
                  </View>

                  {client && (
                    <View style={{ width: "100%", marginTop: 14 }}>
                      <View style={styles.folderHeaderRow}>
                        <Text style={Fonts.medium14}>{t("label-string")}</Text>
                        <Text style={styles.optionalText}>
                          {t("optional-string")}
                        </Text>
                      </View>
                      <View
                        style={[
                          styles.folderContainer,
                          isFolderHovered && styles.folderContainerHovered,
                        ]}
                        onMouseEnter={() =>
                          !isMobile && setIsFolderHovered(true)
                        }
                        onMouseLeave={() => setIsFolderHovered(false)}
                      >
                        <TextInput
                          style={[
                            styles.folderInput,
                            { fontSize: isMobile ? 16 : undefined },
                          ]}
                          placeholder={t("macrocyclePlaceholder-string")}
                          placeholderTextColor={Colors.grayColor}
                          value={labelName}
                          onChangeText={setLabelName}
                          maxLength={20}
                          onKeyPress={handleKeyPress}
                        />

                        <Observer>
                          {() => {
                            const filteredLabels = client.getFilteredLabels(
                              labelName
                            );
                            const hasFolderSuggestions = filteredLabels.length > 0;
                            const hasTypedFolder =
                              String(labelName ?? "").trim() !== "";

                            if (!hasFolderSuggestions && !hasTypedFolder) {
                              return null;
                            }

                            return (
                              <>
                                <View style={styles.folderDivider} />
                                {hasFolderSuggestions ? (
                                  <ScrollView
                                    style={styles.folderSuggestionsScroll}
                                    contentContainerStyle={styles.folderSuggestionsWrap}
                                    showsVerticalScrollIndicator={true}
                                  >
                                    {filteredLabels.map((label, index) => (
                                      <FolderSuggestionButton
                                        label={label}
                                        key={`${label}-${index}`}
                                      />
                                    ))}
                                  </ScrollView>
                                ) : (
                                  <Text style={styles.folderEmptyText}>
                                    {t("labelEmptyText-string")}
                                  </Text>
                                )}
                              </>
                            );
                          }}
                        </Observer>
                      </View>
                    </View>
                  )}
                </View>
                <View
                  style={[
                    GlobalStyles.HStack,
                    {
                      width: "100%",
                      justifyContent: "space-evenly",
                      marginTop: 16,
                    },
                  ]}
                >
                  {block && (
                    <View style={{ position: "absolute", left: 0 }}>
                      <DeleteBlockButton />
                    </View>
                  )}

                  <Buttons bool={false} />
                  <Buttons bool={true} />
                </View>
              </ScrollView>
            </View>
          </View>
        </View>
      </Modal>
    );
  }
);

const styles = StyleSheet.create({
  modalView: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    paddingHorizontal: 24,
    paddingVertical: 24,
    alignItems: "center",
    justifyContent: "space-between",
    maxHeight: "100%",
    maxWidth: "100%",
  },
  weeksRow: {
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 10,
    width: "100%",
    flexDirection: "row",
  },
  weeksColumn: {
    alignItems: "center",
  },
  weeksControl: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    height: 40,
  },
  weeksControlHovered: {},
  weekArrowButton: {
    width: 26,
    height: 26,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    backgroundColor: "transparent",
  },
  weekInput: {
    width: 48,
    minWidth: 48,
    height: 40,
    borderRadius: 12,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    textAlign: "center",
    fontSize: 14,
    fontWeight: "400",
    color: Colors.blackColor,
    paddingHorizontal: 0,
    outlineStyle: "none",
  },
  weekRangePreviewRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginTop: 4,
  },
  weekRangePreviewText: {
    color: Colors.grayColor,
    fontSize: 12,
  },
  folderHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  optionalText: {
    fontSize: 12,
    color: Colors.grayColor,
  },
  folderContainer: {
    width: "100%",
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    borderWidth: 0.1,
    borderRadius: 12,
    overflow: "hidden",
  },
  folderContainerHovered: {
    borderColor: "rgba(0, 0, 0, 0.45)",
  },
  folderInput: {
    height: 40,
    color: Colors.blackColor,
    paddingHorizontal: 12,
    outlineStyle: "none",
  },
  folderDivider: {
    height: 1,
    backgroundColor: "rgba(0, 0, 0, 0.09)",
    width: "100%",
  },
  folderSuggestionsScroll: {
    maxHeight: 140,
  },
  folderSuggestionsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    padding: 10,
  },
  folderSuggestionChip: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    maxWidth: 170,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  folderSuggestionChipHovered: {
    borderColor: "rgba(0, 0, 0, 0.4)",
    backgroundColor: Colors.hoveredGray,
  },
  folderSuggestionChipSelected: {
    borderColor: Colors.bubbleColor,
    backgroundColor: Colors.bubbleColor,
  },
  folderSuggestionContentRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  folderSuggestionIcon: {
    marginRight: 4,
  },
  folderSuggestionText: {
    fontSize: 12,
    color: Colors.darkgrayColor,
    fontWeight: "500",
    flexShrink: 1,
  },
  folderSuggestionTextHovered: {
    color: Colors.blackColor,
  },
  folderSuggestionTextSelected: {
    color: Colors.whiteColor,
  },
  folderEmptyText: {
    alignSelf: "center",
    textAlign: "center",
    color: "rgba(0, 0, 0, 0.4)",
    fontSize: 12,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
});

export default AddCalendarBlockView;
